<template>
  <div
    class="rs-code-input"
    :class="{ 'rs-code-input--error': hasError }"
  >
    <input
      v-for="(digit, index) in digits"
      :key="index"
      ref="inputs"
      :value="digit"
      :aria-label="`Digit ${index + 1}`"
      maxlength="1"
      inputmode="numeric"
      pattern="[0-9]*"
      autocomplete="one-time-code"
      class="rs-code-input__digit"
      :disabled="disabled"
      @input="onInput(index, $event)"
      @keydown.backspace="onBackspace(index, $event)"
      @paste="onPaste(index, $event)"
    >
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

/**
 * A row of single-digit boxes for one-time codes. `v-model` is the code as a string.
 */
export default defineComponent({
  name: 'RsFormCodeInput',

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    length: {
      type: Number,
      default: 6,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  computed: {
    digits(): string[] {
      return Array.from({ length: this.length }, (_, i) => this.modelValue[i] ?? '');
    },
  },

  methods: {
    setDigits(digits: string[]) {
      this.$emit('update:modelValue', digits.join('').slice(0, this.length));
    },

    onInput(index: number, event: Event) {
      const input = event.target as HTMLInputElement;
      const value = input.value.replace(/\D/g, '').slice(-1);
      input.value = value;
      const digits = [ ...this.digits ];
      digits[index] = value;
      this.setDigits(digits);
      if (value) this.focusIndex(index + 1);
    },

    // Moves back to the previous box when backspacing in an empty one.
    onBackspace(index: number, event: KeyboardEvent) {
      if (!(event.target as HTMLInputElement).value && index > 0) {
        this.focusIndex(index - 1);
      }
    },

    // Lets the whole code be pasted into any box.
    onPaste(index: number, event: ClipboardEvent) {
      event.preventDefault();
      const pasted = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
      if (!pasted) return;
      const digits = [ ...this.digits ];
      pasted.split('').forEach((digit, i) => {
        if (index + i < this.length) digits[index + i] = digit;
      });
      this.setDigits(digits);
      this.focusIndex(Math.min(index + pasted.length, this.length - 1));
    },

    focusIndex(index: number) {
      this.$nextTick(() => {
        const inputs = this.$refs.inputs as HTMLInputElement[] | undefined;
        inputs?.[Math.min(index, this.length - 1)]?.focus();
      });
    },

    // Focuses the first empty box, or the last one when the code is complete.
    focus() {
      const firstEmpty = this.digits.findIndex((digit) => digit === '');
      this.focusIndex(firstEmpty === -1 ? this.length - 1 : firstEmpty);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.rs-code-input {
  display: flex;
  gap: 0.5rem;
}
.rs-code-input__digit {
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  color: $tertiary-dark;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}
.rs-code-input--error .rs-code-input__digit {
  border-color: $error-dark;
}
</style>
